// @ts-nocheck

import { History } from 'history';
import React, { useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { CompactSvg } from '../../assets/svg/history';
import { UnfoldedSvg } from '../../assets/svg/history';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { BetslipFilters, type BetslipFilter } from '../../types/history';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import FiltersWrapper from '../FiltersWrapper';
import MatchBetFilter from '../MatchBetFilter';
import StickyScrollWrapper from '../StickyScrollWrapper';

const Wrapper = styled(StickyScrollWrapper)`
  display: flex;
  align-items: center;
`;

const StyledFiltersWrapper = styled(FiltersWrapper)`
  &&& {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const StyledFilterButton = styled(MatchBetFilter)`
  font-size: 1em;
  padding: 0.4em 0.77em;

  ${({ $isSelected }) =>
    !$isSelected
      ? css`
          border-color: ${theme.History.historyFilterButton.border};
        `
      : null}
`;

const Switch = styled.div`
  position: relative;
  z-index: 1;
  background: ${theme.History.historyFilterButton.switchBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2.3em;
  cursor: pointer;
`;

const SwitchIcon = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  padding: 0.53em 0.63em;
  width: 2.33em;
  height: 1.99em;

  transition: color 200ms ease-in-out;
  color: ${({ $active }) => theme.History.historyFilterButton[$active ? 'switchActiveColor' : 'switchColor']};
`;

const StyledSvg = styled(CompactSvg)`
  width: 100%;
  height: 100%;
`;

const ActiveSwitch = styled.div`
  position: absolute;
  z-index: 2;
  top: 0.13em;
  left: 0.13em;
  width: calc(50% - 0.13em * 2);
  height: calc(100% - 0.13em * 2);
  border-radius: 1.53em;
  background: ${theme.History.historyFilterButton.switchActiveBackground};

  transition: transform 200ms ease-in-out;

  ${({ $isCompact }) =>
    !$isCompact
      ? css`
          transform: translateX(100%) translateX(0.26em);
        `
      : css`
          transform: translateX(0);
        `}
`;

const filterOptions = [
  // __('Saved')
  { type: BetslipFilters.FAVORITES, labelKey: 'Saved' },
  // __('Ongoing')
  { type: BetslipFilters.RUNNING, labelKey: 'Ongoing' },
  // __('!plural:Won')
  { type: BetslipFilters.WON, labelKey: '!plural:Won' },
  // __('!plural:Finished')
  { type: BetslipFilters.SETTLED, labelKey: '!plural:Finished' },
  // __('All')
  { type: null, labelKey: 'All' },
  // __('Hidden')
  { type: BetslipFilters.HIDDEN, labelKey: 'Hidden' },
];

type HistoryFiltersBarProps = {
  className?: string;
  history: History;
  currentFilter: BetslipFilter;
  isCompact: boolean;
  setIsCompact: (...params: unknown) => unknown;
};

const HistoryFiltersBar = ({ className, currentFilter, isCompact, setIsCompact }: HistoryFiltersBarProps) => {
  const filtersElementsRef = useRef([]);
  const history = useHistory();

  const onChange = useCallback(
    (filter) => {
      if (filter) {
        history.replace(RoutesRedirectionFactory.getHistoryRouteConfig(filter));
      } else {
        history.replace(RoutesRedirectionFactory.getHistoryRouteConfig());
      }
    },
    [history],
  );

  return (
    <Wrapper className={className} data-testid="history-status-filter">
      <Switch>
        <ActiveSwitch $isCompact={isCompact} />
        <SwitchIcon $active={isCompact} onClick={() => setIsCompact(true)}>
          <StyledSvg as={CompactSvg} />
        </SwitchIcon>
        <SwitchIcon $active={!isCompact} onClick={() => setIsCompact(false)}>
          <StyledSvg as={UnfoldedSvg} />
        </SwitchIcon>
      </Switch>
      <StyledFiltersWrapper
        filtersElementsRef={filtersElementsRef}
        className={className}
        hasAutoStickyBehaviour={false}
      >
        {filterOptions.map(({ type, labelKey }, index) => {
          const isSelected = type === currentFilter;

          return (
            <StyledFilterButton
              key={`filter-${index}`}
              id={type}
              label={__(labelKey)}
              ref={(el) => (filtersElementsRef.current[index] = el)}
              onClick={() => onChange(type)}
              isSelected={isSelected}
              $isSelected={isSelected}
              data-testid={`filter-${isSelected ? 'selected' : type || '0'}`}
            />
          );
        })}
      </StyledFiltersWrapper>
    </Wrapper>
  );
};

export default HistoryFiltersBar;
